import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { PageHeader, Card, EmptyState } from "../../components/UI";
import { ArrowLeft, Images, MapPin, Loader2, User } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PhotoGallery() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/political-intelligence/candidates/${id}/photos`)
      .then(r => setData(r.data))
      .catch(() => setData({ candidate_name: "", photos: [] }));
  }, [id]);

  if (!data) {
    return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;
  }

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="photo-gallery-page">
      <button onClick={() => navigate(`/political-intelligence/${id}`)} data-testid="gallery-back-btn"
        className="text-sm text-slate-400 hover:text-cyan-300 inline-flex items-center gap-1.5 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to {data.candidate_name || "candidate"} dashboard
      </button>
      <PageHeader
        eyebrow="Field Intelligence"
        title="Volunteer Photo Gallery"
        subtitle={`Every ad and mailer photo captured by volunteers for the ${data.candidate_name} race — newest first.`}
      />
      {data.photos.length === 0 ? (
        <EmptyState icon={Images} title="No photos yet"
          description="When volunteers attach photos of spotted ads or mailers through the volunteer portal, they'll appear here." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {data.photos.map(p => (
            <Card key={p.id} padded={false} className="overflow-hidden group" data-testid={`gallery-photo-${p.id}`}>
              <a href={`${BACKEND}${p.photo_url}`} target="_blank" rel="noreferrer" className="block">
                <img src={`${BACKEND}${p.photo_url}`} alt={`${p.subject_name} ${p.channel} sighting`}
                  className="w-full h-44 object-cover group-hover:opacity-90 transition-opacity" loading="lazy" />
              </a>
              <div className="p-3 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-white truncate">{p.subject_name}</span>
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-cyan-400/30 text-cyan-300 shrink-0">{p.channel}</span>
                </div>
                <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
                  <span>{p.date}</span>
                  {p.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{p.location}</span>}
                  {p.reporter_name && <span className="inline-flex items-center gap-1"><User className="w-3 h-3" />{p.reporter_name}</span>}
                </div>
                {p.note && <p className="text-xs text-slate-400 line-clamp-2">{p.note}</p>}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
